import {
  hasCleanFile,
  replacementPending,
  type DocumentRow,
  type Lifecycle,
} from './documentLabels';

/**
 * What a row offers, in menu order (design.md, "Actions in one table").
 *
 * Worked out from the row and whether the person may write, in one place,
 * so the row menu and the drawer cannot disagree and a test can pin every
 * line of the table (I2, I3).
 */
export type DocumentActionId =
  | 'edit'
  | 'open'
  | 'download'
  | 'publish'
  | 'unpublish'
  | 'republish'
  | 'replace'
  | 'remove';

/** The menu's words, in menu order. */
export const ACTION_LABELS: Record<DocumentActionId, string> = {
  edit: 'Edit',
  open: 'Open',
  download: 'Download',
  publish: 'Publish',
  unpublish: 'Unpublish',
  republish: 'Republish',
  replace: 'Replace file',
  remove: 'Remove',
};

/** The three lifecycle moves and Remove, as the backend's one verb takes them. */
export type TransitionTarget = Exclude<Lifecycle, 'DRAFT'>;

/**
 * The lifecycle actions and where each one goes, for the row menu and the
 * drawer alike. Remove is not here: it asks first.
 */
export const TRANSITIONS: Partial<Record<DocumentActionId, TransitionTarget>> = {
  publish: 'PUBLISHED',
  unpublish: 'INACTIVE',
  republish: 'PUBLISHED',
};

export function actionsFor(row: DocumentRow, writable: boolean): DocumentActionId[] {
  // A removed row - by hand or by the scan - is read-only and holds no file.
  if (row.lifecycle === 'ARCHIVED') return [];

  const clean = hasCleanFile(row);
  const actions: DocumentActionId[] = [];

  if (writable) actions.push('edit');
  // Reads, offered to anybody who can see the library, only where there is a
  // clean file to link (backend D38).
  if (clean) actions.push('open', 'download');
  if (!writable) return actions;

  if (row.lifecycle === 'DRAFT' && clean) actions.push('publish');
  if (row.lifecycle === 'PUBLISHED') actions.push('unpublish');
  if (row.lifecycle === 'INACTIVE') actions.push('republish');
  // One pending object per document (backend I4): no second replacement
  // while one scans, and none at all until the first file is clean.
  if (clean && !replacementPending(row)) actions.push('replace');
  actions.push('remove');
  return actions;
}
